<template>
  <div class="text-left">
    <BreadcrumbsNav />

    <!-- Hero -->
    <v-row align="center" class="mb-8">
      <v-col cols="12" md="8">
        <h1 class="text-h4 font-weight-black mb-4">
          Espace Gestionnaire
          <br />
          <span class="primary--text">Pilotez, mesurez, économisez</span>
        </h1>
        <p class="text-subtitle-1 mb-4">
          Mairies, services enfance, chefs de salle, référents scolaires, cuisines centrales — Labricot centralise
          toutes les informations clés pour réduire le gaspillage alimentaire, adapter les quantités et piloter votre
          conformité EGAlim avec des données réelles.
        </p>
        <div class="d-flex flex-wrap mb-4" style="gap: 8px">
          <v-chip color="primary" small outlined>Gestion des effectifs</v-chip>
          <v-chip color="primary" small outlined>Anti-gaspillage</v-chip>
          <v-chip color="primary" small outlined>Conformité EGAlim</v-chip>
          <v-chip color="primary" small outlined>Communication multi-acteurs</v-chip>
        </div>
        <div class="d-flex flex-wrap" style="gap: 12px">
          <v-btn color="primary" large href="/creer-mon-compte">Créer mon compte</v-btn>
          <v-btn color="primary" large outlined :to="{ name: 'ContactPage' }">Voir la démo</v-btn>
        </div>
      </v-col>
      <v-col cols="12" md="4">
        <v-img
          src="/static/images/dashboard-restaurateur.png"
          contain
          max-width="100%"
          class="mx-auto rounded labricot-img"
        />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 1 — Effectifs & Absences -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6" order-md="1" order="2">
        <v-chip color="primary" outlined small class="mb-3">Référent scolaire · Chef de salle</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Adaptez les quantités grâce aux absences du jour</h2>
        <p>
          Consultez les absences par niveau en quelques clics. Le chef de salle sait exactement combien de barquettes
          mettre en chauffe — sans surproduction ni manque. Finies les estimations à l'aveugle.
        </p>
        <ul class="mt-2 mb-4">
          <li>Saisie des absences par classe ou niveau</li>
          <li>Nombre d'élèves attendus mis à jour en temps réel</li>
          <li>Données transmises automatiquement au chef de salle</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : moins de restes dans les bacs, zéro surproduction
        </v-chip>
      </v-col>
      <v-col cols="12" md="6" order-md="2" order="1">
        <!-- TODO: remplacer src par la capture du module Effectifs (dashboard chef de salle) -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 2 — Communication multi-acteurs -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6">
        <!-- TODO: remplacer src par la capture du panel Communications (dashboard superviseur) -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
      <v-col cols="12" md="6">
        <v-chip color="primary" outlined small class="mb-3">École · Cantine · Cuisine centrale</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Un canal direct entre tous les acteurs du repas</h2>
        <p>
          Absences du jour, spécificités alimentaires de groupes, retours sur les plats, difficultés de service — toutes
          ces informations sont centralisées dans un outil partagé entre l'école, la cantine et le prestataire de
          cuisine.
        </p>
        <ul class="mt-2 mb-4">
          <li>Messages et alertes visibles par chaque rôle</li>
          <li>Feedback quotidien transmis automatiquement à la cuisine centrale</li>
          <li>Dialogue factuel basé sur des données, non sur des impressions</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : moins de repas inadaptés, meilleure adéquation aux besoins réels
        </v-chip>
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 3 — Gestion des surplus & valorisation -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6" order-md="1" order="2">
        <v-chip color="primary" outlined small class="mb-3">Chef de salle · Animateur</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Signalez les surplus, valorisez les excédents</h2>
        <p>
          Les plats non sortis du froid ou les denrées propres à la consommation ne trouvent pas preneur ? Signalez les
          excédents en 1 clic pour informer les associations partenaires et les banques alimentaires.
        </p>
        <ul class="mt-2 mb-4">
          <li>Déclaration des excédents alimentaires du service</li>
          <li>Mise en relation avec les circuits de don adaptés</li>
          <li>Respect des bonnes pratiques d'hygiène</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : baisse des coûts de collecte des déchets, démarche solidaire valorisée
        </v-chip>
      </v-col>
      <v-col cols="12" md="6" order-md="2" order="1">
        <v-img src="/static/images/lighthouse.png" contain max-width="100%" class="mx-auto rounded labricot-img" />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 4 — Dashboard & Conformité EGAlim -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6">
        <!-- TODO: remplacer src par la capture du dashboard statistiques / conformité EGAlim -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
      <v-col cols="12" md="6">
        <v-chip color="primary" outlined small class="mb-3">Directeurs · Élus · Cuisines centrales</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Des chiffres pour piloter et se mettre en conformité EGAlim</h2>
        <p>
          Tableaux de bord lisibles, statistiques de satisfaction, suivi du gaspillage semaine par semaine — les
          décideurs disposent de données concrètes pour communiquer et démontrer leurs efforts.
        </p>
        <ul class="mt-2 mb-4">
          <li>Suivi qualitatif et quantitatif du gaspillage alimentaire</li>
          <li>Statistiques d'appréciation des plats et menus</li>
          <li>Aide à la télédéclaration EGAlim et au pacte anti-gaspillage</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : conformité EGAlim sans angoisse, chiffres à l'appui
        </v-chip>
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- CTA final -->
    <v-row justify="center" class="my-12 text-center">
      <v-col cols="12" md="8">
        <h2 class="text-h5 font-weight-bold mb-4">Prêt à transformer votre gestion de cantine ?</h2>
        <p class="mb-6">Rejoignez les établissements qui pilotent leur restauration avec des données réelles.</p>
        <div class="d-flex flex-wrap justify-center" style="gap: 12px">
          <v-btn color="primary" large href="/creer-mon-compte">Créer mon compte</v-btn>
          <v-btn color="primary" large outlined :to="{ name: 'ContactPage' }">Contacter un conseiller</v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BreadcrumbsNav from "@/components/BreadcrumbsNav"

export default {
  name: "GestionnairePage",
  components: { BreadcrumbsNav },
}
</script>

<style scoped>
.labricot-img {
  max-height: 400px;
  width: auto;
}
.placeholder-img {
  min-height: 220px;
  background-color: #f5f5f5;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
}
@media (max-width: 600px) {
  .labricot-img {
    max-width: 320px;
    height: auto;
    max-height: none;
  }
}
</style>
